
import type CreatePortalUserDto from "~/models/CreatePortalUserDto";
import AccessGroupCustomerEnum from "~/models/AccessGroupCustomerEnum";
import PortalUserPermissionDto from "~/models/PortalUserPermissionDto";
import PermissionEnum from "~/models/PermissionEnum";
import type PortalUserDto from "~/models/PortalUserDto";
import type { AnybillResult } from "~/additionalModels/AnybillResponse";

export const usePortalUsersModule = defineRequestStore("portalUsers", () => {
  const userModule = useUserModule();
  const { userRights } = storeToRefs(userModule);

  const loading = ref(false);
  const users = ref<PortalUserDto[] | null>(null);
  const initialized = computed(() => !!users.value);

  /* INTERNAL FUNCTIONS */
  function _setUsers(pUsers: PortalUserDto[] | null) {
    users.value = pUsers;
  }

  function _addUser(user: PortalUserDto) {
    users.value!.push(user);
  }

  function _removeUser(id: string) {
    users.value = users.value!.filter(c => c.id !== id);
  }

  function _updateUser(u: PortalUserDto) {
    const index = users.value!.findIndex(user => user.id === u.id);
    if (index !== -1)
      users.value![index] = u;
  }

  function _setLoading(newState: boolean) {
    loading.value = newState;
  }

  /* PUBLIC FUNCTIONS */
  async function reset() {
    _setUsers(null);
  }

  async function populate() {
    _setLoading(true);
    if (userRights.value?.get(AccessGroupCustomerEnum.PortalUser) ?? 0 >= 1) {
      const response = await useTypedFetch<AnybillResult<PortalUserDto[]>>("/userPortaluserService/get?skip=0&take=100");
      if (response.success) {
        _setUsers(response.value);
      }
      else {
        await AnybillLogger.instance.error(
        `Population call failed while trying to get available portal users`,
        new Error(response.errorMessage ?? "unknown"),
        "portalUsers store",
        );
      }
    }
    else {
      _setUsers([]);
    }

    _setLoading(false);
  }

  /**
   * Creates a new portal user. Returns the full AnybillResult (rather than collapsing failures to
   * null) so callers can surface the actual backend error message/code — e.g. "user-alreadyexists"
   * when the username is already registered — instead of only a generic error.
   */
  async function addUser(user: CreatePortalUserDto): Promise<AnybillResult<PortalUserDto>> {
    _setLoading(true);
    for (const p of user.permissions!) {
      Object.assign(p, new PortalUserPermissionDto({
        accessGroup: Object.keys(AccessGroupCustomerEnum).indexOf(AccessGroupCustomerEnum[p.accessGroup ?? "None"]) as any,
        permission: Object.keys(PermissionEnum).indexOf(p.permission) as any,
      }));
    }

    const response = await useTypedFetch<AnybillResult<PortalUserDto>>("/userPortaluserService/post", {
      body: {
        user,
      },
    });
    if (response.success) {
      _addUser(response.value);
    }
    else {
      await AnybillLogger.instance.warn(
        `Creation call failed while trying to create user with username ${user.username}`,
        new Error(response.errorMessage ?? "unknown"),
        "portalUsers store",
      );
    }
    _setLoading(false);

    return response;
  }

  async function updateUserPermissions(user: PortalUserDto): Promise<void> {
    _setLoading(true);
    for (const p of user.permissions!) {
      Object.assign(p, new PortalUserPermissionDto({
        accessGroup: Object.keys(AccessGroupCustomerEnum).indexOf(AccessGroupCustomerEnum[p.accessGroup ?? "None"]) as any,
        permission: Object.keys(PermissionEnum).indexOf(p.permission) as any,
      }));
    }
    user.permissions = user.permissions?.map(p => new PortalUserPermissionDto(p)) ?? [];
    const response = await useTypedFetch<AnybillResult<PortalUserDto>>("/userPortaluserService/put", {
      user,

    });
    if (response.success) {
      _updateUser(user);
    }
    else {
      await AnybillLogger.instance.error(
        `Update call failed while trying to update user permissions of user ${user.id}`,
        new Error(response.errorMessage ?? "unknown"),
        "portalUsers store",
      );
    }

    _setLoading(false);
  }

  async function deleteUser(user: PortalUserDto) {
    _setLoading(true);

    const response = await useTypedFetch<AnybillResult<PortalUserDto>>("/userPortaluserSpecificService/delete", {
      portalUserId: user.id,
    });

    if (response.success) {
      _removeUser(user.id);
    }
    else {
      await AnybillLogger.instance.error(
        `Deletion call failed while trying to delete user ${user.id}`,
        new Error(response.errorMessage ?? "unknown"),
        "portalUsers store",
      );
    }

    _setLoading(false);
  }

  return { initialized, loading, reset, populate, users, addUser, updateUserPermissions, deleteUser };
});
